import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query"
import {
  PRODUCTS_LIST_DEFAULT_LIMIT,
  routes,
  type MasterCategory,
  type MarketplaceId,
  type ProductCatalogMarketplaceState,
  type ProductCatalogOfferState,
  type ProductFlagFilter,
  type ProductsListResponse,
  type StoreCode,
} from "@nova-ai/shared"
import { useEffect, useRef } from "react"

import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

/** Everything that decides WHICH products the page is about. Marketplace state
 *  is separate because its counts span all states in this scope. */
export type ProductsScope = {
  store?: StoreCode
  category?: MasterCategory
  query?: string
  ownBrand?: ProductFlagFilter
  live?: ProductFlagFilter
  marketplace?: MarketplaceId
}

export type ProductsFilter = ProductsScope & {
  marketplaceState?: ProductCatalogMarketplaceState
  offerState?: ProductCatalogOfferState
}

type ProductsPageParam = {
  offset: number
  offerSnapshotVersion?: string
}

const INITIAL_PRODUCTS_PAGE: ProductsPageParam = { offset: 0 }

class ProductsSnapshotConflictError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "ProductsSnapshotConflictError"
  }
}

function shouldRetryProductsRequest(failureCount: number, error: Error): boolean {
  return !(error instanceof ProductsSnapshotConflictError) && failureCount < 1
}

function flagParam(
  key: keyof ProductsFilter,
  value: ProductFlagFilter | undefined
): Record<string, string> {
  return value === undefined || value === "any" ? {} : { [key]: value }
}

// `any` is the API default — omit it so the request URL only carries the axes
// the operator actually narrowed.
function scopeParams(scope: ProductsScope): Record<string, string> {
  return {
    ...(scope.marketplace === undefined && scope.store !== undefined ? { store: scope.store } : {}),
    ...(scope.category !== undefined ? { category: scope.category } : {}),
    ...(scope.query !== undefined ? { q: scope.query } : {}),
    ...flagParam("ownBrand", scope.ownBrand),
    ...flagParam("live", scope.live),
    ...(scope.marketplace !== undefined ? { marketplace: scope.marketplace } : {}),
  }
}

export function useProducts(filter: ProductsFilter) {
  const queryClient = useQueryClient()
  const queryKey = queryKeys.products(filter)
  const recoveredConflict = useRef<Error | null>(null)
  const query = useInfiniteQuery({
    queryKey,
    initialPageParam: INITIAL_PRODUCTS_PAGE,
    queryFn: async ({ pageParam, signal }) => {
      const result = await apiFetch<ProductsListResponse>(routes.products.list(), {
        signal,
        query: {
          limit: String(PRODUCTS_LIST_DEFAULT_LIMIT),
          offset: String(pageParam.offset),
          ...(pageParam.offerSnapshotVersion !== undefined
            ? { offerSnapshotVersion: pageParam.offerSnapshotVersion }
            : {}),
          ...scopeParams(filter),
          ...(filter.marketplaceState !== undefined && filter.marketplaceState !== "all"
            ? { marketplaceState: filter.marketplaceState }
            : {}),
          ...(filter.offerState !== undefined && filter.offerState !== "all"
            ? { offerState: filter.offerState }
            : {}),
        },
      })
      if (!result.ok) {
        if (pageParam.offset > 0 && result.error.kind === "conflict") {
          throw new ProductsSnapshotConflictError(result.error.message)
        }
        throw new Error(result.error.message)
      }
      return result.value
    },
    // Preserve the dashboard's normal one retry for transient failures. A stale
    // snapshot token is deterministic, so never send that exact token twice.
    retry: shouldRetryProductsRequest,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore
        ? {
            offset: lastPage.pagination.offset + lastPage.pagination.limit,
            ...(lastPage.offerSnapshotVersion !== undefined
              ? { offerSnapshotVersion: lastPage.offerSnapshotVersion }
              : {}),
          }
        : undefined,
  })

  useEffect(() => {
    if (!(query.error instanceof ProductsSnapshotConflictError)) {
      recoveredConflict.current = null
      return
    }
    if (recoveredConflict.current === query.error) {
      return
    }
    recoveredConflict.current = query.error
    // Reset removes every page and pageParam, including the stale token. Active
    // queries immediately start again at INITIAL_PRODUCTS_PAGE (offset zero).
    void queryClient.resetQueries({ queryKey, exact: true })
  }, [query.error, queryClient, queryKey])

  return query
}
